// Flow 2 - the hardened Validate context upload (D77; docs/phase-0/slice5-ux-flows.md §5;
// docs/phase-0/slice5-component-designs.md §3.2). Reuses the EXACT composition Certify's
// own data-room upload already ships (`DataRoom.tsx`, `slice4-certify-ux.md §5.1`): the
// standing privacy notice renders BEFORE any file is chosen, a real `<label>`-associated
// file input is the primary, keyboard-operable path (`.dropzone`, drag-and-drop is an
// enhancement only, WCAG 2.5.7), and a rejected file is a SUCCESSFUL response
// (`ValidateUploadResult`'s `state: "rejected"`, model/validate.ts:171-189) - only a
// genuine transfer/infra failure (network drop, 5xx) is the distinct "transfer failed"
// state, never leaking scanner internals either way (D77).
import { useId, useRef, useState } from "react";
import { isErr } from "@nightcrew/shared";
import type { AppId } from "@nightcrew/shared";
import { uploadValidateContext } from "../api/validate.js";
import { validateCopy } from "../copy/validate.js";

// Illustrative caps (ASSUMED, flagged - same convention as `DataRoom.tsx`'s own
// `MAX_SIZE_MB`/`ACCEPTED_TYPES`: no canon doc pins a confirmed value for the Validate
// context-upload size/type limits).
const MAX_SIZE_MB = 20;
const ACCEPTED_TYPES = [
  "application/pdf",
  "text/plain",
  "text/markdown",
  "image/png",
  "image/jpeg",
];

/** The perceived delay before the "uploading" meter switches to the distinct "scanning"
 * meter treatment (ux spec §5.2's two-meter convention). A single awaited request cannot
 * observe the real transfer-complete/scan-start boundary, so this is a plain, honest
 * staging heuristic (never a fabricated percentage) - flagged as ASSUMED timing, not a
 * measured value. */
const SCANNING_TRANSITION_MS = 600;

type UploadState =
  | { readonly status: "idle" }
  | { readonly status: "dragging" }
  | { readonly status: "selected"; readonly file: File }
  | { readonly status: "rejected_client"; readonly reason: "type" | "size" }
  | { readonly status: "uploading"; readonly file: File }
  | { readonly status: "scanning"; readonly file: File }
  | { readonly status: "transfer_failed"; readonly file: File }
  | {
      readonly status: "rejected_server";
      readonly copyKey:
        | "validate.upload.rejectedMalware"
        | "validate.upload.rejectedStructure"
        | "validate.upload.rejectedContent";
    }
  | { readonly status: "accepted"; readonly filename: string };

export const UploadContextPanel = ({
  appId,
  canManage,
  onDone,
}: {
  readonly appId: AppId;
  /** Owner/admin gate (ux spec §5.2's "Denied" row - defense in depth, since Talk-to-Glim
   * itself is already owner/admin-gated). */
  readonly canManage: boolean;
  readonly onDone: () => void;
}): React.JSX.Element => {
  const [state, setState] = useState<UploadState>({ status: "idle" });
  const fileInputId = useId();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const validateAndSelect = (file: File): void => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setState({ status: "rejected_client", reason: "type" });
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setState({ status: "rejected_client", reason: "size" });
      return;
    }
    setState({ status: "selected", file });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file !== undefined) validateAndSelect(file);
  };

  const startUpload = (file: File): void => {
    setState({ status: "uploading", file });
    timerRef.current = setTimeout(() => {
      setState((prev) => (prev.status === "uploading" ? { status: "scanning", file } : prev));
    }, SCANNING_TRANSITION_MS);

    void (async () => {
      const result = await uploadValidateContext(appId, file);
      if (timerRef.current !== null) clearTimeout(timerRef.current);

      if (isErr(result)) {
        setState({ status: "transfer_failed", file });
        return;
      }
      if (result.value.state === "accepted") {
        setState({ status: "accepted", filename: file.name });
        return;
      }
      // A rejected result is a SUCCESSFUL response (model/validate.ts:171-189) - never
      // an error branch. `blocked_content` covers both a direct content block and a
      // clean-scanned, abuse-routed file: the founder-visible copy is identical for
      // both by design (ux spec §5.2 - never reveal that routing happened).
      const copyKey =
        result.value.scan === "blocked_malware"
          ? "validate.upload.rejectedMalware"
          : result.value.scan === "blocked_structure"
            ? "validate.upload.rejectedStructure"
            : "validate.upload.rejectedContent";
      setState({ status: "rejected_server", copyKey });
    })();
  };

  const retrySameFile = (file: File): void => {
    startUpload(file);
  };

  return (
    <div className="card pad upload-context-panel" data-testid="upload-context-panel">
      <div className="row between aic gap8">
        <h2 className="h3">{validateCopy("validate.upload.heading")}</h2>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onDone}>
          {validateCopy("validate.upload.doneCta")}
        </button>
      </div>

      {/* The privacy affordance renders BEFORE any file is chosen, standing and
          non-collapsible (§5.1). */}
      <div className="well pad row aic gap8 mt12" data-testid="privacy-affordance">
        <span aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="5" y="11" width="14" height="9" rx="1.5" />
            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
          </svg>
        </span>
        <span style={{ fontSize: 12.5 }}>{validateCopy("validate.upload.privacyNotice")}</span>
      </div>

      {!canManage ? (
        <p className="muted mt12" style={{ fontSize: 13 }}>
          {validateCopy("validate.upload.deniedNotOwnerAdmin")}
        </p>
      ) : (
        <>
          {state.status === "idle" ||
          state.status === "dragging" ||
          state.status === "rejected_client" ? (
            <div
              className={`dropzone mt12${state.status === "dragging" ? " dragging" : ""}`}
              onDragOver={(event) => {
                event.preventDefault();
                setState({ status: "dragging" });
              }}
              onDragLeave={() => {
                setState({ status: "idle" });
              }}
              onDrop={(event) => {
                event.preventDefault();
                const file = event.dataTransfer.files[0];
                if (file !== undefined) validateAndSelect(file);
              }}
            >
              <p className="muted" style={{ fontSize: 13 }}>
                {state.status === "dragging"
                  ? validateCopy("validate.upload.dragOver")
                  : validateCopy("validate.upload.dropInstruction")}
              </p>
              <label className="btn btn-ghost btn-sm" htmlFor={fileInputId}>
                {validateCopy("validate.upload.chooseFileCta")}
              </label>
              <input
                id={fileInputId}
                type="file"
                // Deliberately NO `accept` attribute: `accept` is only an OS file-picker
                // hint, never a real gate (a founder can always choose "All files"), so
                // this component's own `validateAndSelect` check below is the actual
                // client-side wall - restating it via `accept` would let the browser's
                // (and jsdom/user-event's) file-picker simulation silently swallow a
                // mismatched file before that real check ever runs.
                className="sr-only"
                onChange={handleFileChange}
              />
              {state.status === "rejected_client" ? (
                <p className="field-error mt8" role="alert">
                  {validateCopy("validate.upload.wrongType", {
                    types: "PDF, text, Markdown, PNG, or JPG",
                    size: `${String(MAX_SIZE_MB)}MB`,
                  })}
                </p>
              ) : null}
            </div>
          ) : null}

          {state.status === "selected" ? (
            <div className="mt12">
              <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>{state.file.name}</p>
              <p className="muted" style={{ fontSize: 12 }}>
                {Math.ceil(state.file.size / 1024)} KB
              </p>
              <button
                type="button"
                className="btn btn-primary mt8"
                onClick={() => {
                  startUpload(state.file);
                }}
              >
                {validateCopy("validate.upload.addCta")}
              </button>
            </div>
          ) : null}

          {state.status === "uploading" ? (
            <div className="mt12" data-testid="upload-progress">
              <div className="meter">
                <span style={{ width: "60%" }} />
              </div>
              <p className="muted mt8" role="status" style={{ fontSize: 12.5 }}>
                {validateCopy("validate.upload.uploading", { filename: state.file.name })}
              </p>
            </div>
          ) : null}

          {state.status === "scanning" ? (
            <div className="mt12" data-testid="upload-scanning">
              <div className="meter bordered">
                <span style={{ width: "100%" }} />
              </div>
              <p className="muted mt8" role="status" style={{ fontSize: 12.5 }}>
                {validateCopy("validate.upload.scanning")}
              </p>
            </div>
          ) : null}

          {state.status === "transfer_failed" ? (
            <div className="mt12" data-testid="upload-transfer-failed">
              <p className="field-error" role="alert">
                {validateCopy("validate.upload.transferFailed")}
              </p>
              {/* Retry hits the SAME POST /v1/apps/:appId/validate/uploads call with the
                  SAME already-selected file - the founder never re-picks it from disk. */}
              <button
                type="button"
                className="btn btn-ghost btn-sm mt8"
                onClick={() => {
                  retrySameFile(state.file);
                }}
              >
                {validateCopy("validate.upload.retrySameCta")}
              </button>
            </div>
          ) : null}

          {state.status === "rejected_server" ? (
            <div className="mt12" data-testid="upload-rejected">
              <p className="field-error" role="alert">
                {validateCopy(state.copyKey)}
              </p>
              <button
                type="button"
                className="btn btn-ghost btn-sm mt8"
                onClick={() => {
                  setState({ status: "idle" });
                }}
              >
                {validateCopy("validate.upload.retryCta")}
              </button>
            </div>
          ) : null}

          {state.status === "accepted" ? (
            <div className="banner good mt12" role="status" data-testid="upload-accepted">
              <span className="grow">
                {validateCopy("validate.upload.accepted", { filename: state.filename })}
              </span>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

